import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/**
 * PARAGON Impersonation Store
 *
 * Manages cross-tenant impersonation state with sessionStorage persistence.
 * When an agency admin impersonates a tenant, the impersonation token is stored
 * here and used by `getActiveSupabaseClient()` to swap the auth header.
 *
 * Security:
 * - Token stored in sessionStorage (cleared on tab close)
 * - Expires after 1 hour (server-enforced)
 * - Clearing impersonation invalidates all cached queries
 */

interface ParagonState {
  /** Short-lived JWT with impersonated_tenant_id claim */
  impersonationToken: string | null
  /** UUID of the tenant being impersonated */
  impersonatedTenantId: string | null
  /** Display name for UI */
  impersonatedTenantName: string | null
  /** When impersonation started (ISO string) */
  impersonationStartedAt: string | null

  /** Begin impersonation session */
  startImpersonation: (
    token: string,
    tenantId: string,
    tenantName: string
  ) => void

  /** End impersonation — clears state and reloads */
  endImpersonation: () => void

  /** Check if currently impersonating */
  isImpersonating: () => boolean
}

export const useParagonStore = create<ParagonState>()(
  persist(
    (set, get) => ({
      impersonationToken: null,
      impersonatedTenantId: null,
      impersonatedTenantName: null,
      impersonationStartedAt: null,

      startImpersonation: (token, tenantId, tenantName) => {
        set({
          impersonationToken: token,
          impersonatedTenantId: tenantId,
          impersonatedTenantName: tenantName,
          impersonationStartedAt: new Date().toISOString(),
        })
      },

      endImpersonation: () => {
        set({
          impersonationToken: null,
          impersonatedTenantId: null,
          impersonatedTenantName: null,
          impersonationStartedAt: null,
        })
        // Hard reload to clear all cached data from impersonated tenant
        window.location.reload()
      },

      isImpersonating: () => {
        const { impersonatedTenantId, impersonationStartedAt } = get()
        if (!impersonatedTenantId || !impersonationStartedAt) return false
        const elapsed = Date.now() - new Date(impersonationStartedAt).getTime()
        if (elapsed >= 60 * 60 * 1000) {
          // Token expired — auto-clear stale impersonation state
          set({
            impersonationToken: null,
            impersonatedTenantId: null,
            impersonatedTenantName: null,
            impersonationStartedAt: null,
          })
          return false
        }
        return true
      },
    }),
    {
      name: 'paragon-impersonation',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist token-related fields, not methods
      partialize: (state) => ({
        impersonationToken: state.impersonationToken,
        impersonatedTenantId: state.impersonatedTenantId,
        impersonatedTenantName: state.impersonatedTenantName,
        impersonationStartedAt: state.impersonationStartedAt,
      }),
    }
  )
)
